/**
 * Lista de resultados da busca
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import { useAppSelector } from '../../hooks/useAppSelector';
import { ExportFormat } from '../../types';

interface ResultsListProps {
  compact?: boolean;
  tableOnly?: boolean;
  sortBy?: 'name' | 'rating' | 'distance';
  filterQuality?: number;
  filterHasWebsite?: boolean;
  filterHasWhatsapp?: boolean;
  filterHasPhone?: boolean;
  filterHasEmail?: boolean;
  onCountsChange?: (resultsCount: number, selectedCount: number) => void;
  exportTrigger?: { type: 'csv' | 'json' } | null;
  onExportComplete?: () => void;
}

const ResultsList: React.FC<ResultsListProps> = ({ 
  compact = false, 
  tableOnly = false,
  sortBy = 'name',
  filterQuality = 0,
  filterHasWebsite = false,
  filterHasWhatsapp = false,
  filterHasPhone = false,
  filterHasEmail = false,
  onCountsChange,
  exportTrigger,
  onExportComplete
}) => {
  const { results, isSearching } = useAppSelector((state) => state.search);
  const viewMode: 'cards' | 'table' = tableOnly ? 'table' : 'cards';
  
  // Seleção de linhas
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Funções de seleção
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(sortedResults.map(b => b.id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const isSelected = (id: string) => selectedRows.has(id);

  const handleExport = (format: ExportFormat) => {
    // Se há seleção, exporta apenas selecionados. Senão, exporta todos
    const dataToExport = selectedRows.size > 0 
      ? results.filter(b => selectedRows.has(b.id))
      : results;

    if (dataToExport.length === 0) {
      alert('Nenhum resultado para exportar');
      return;
    }

    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case ExportFormat.JSON:
        content = JSON.stringify(dataToExport, null, 2);
        filename = selectedRows.size > 0 ? 'resultados_selecionados.json' : 'resultados_busca.json';
        mimeType = 'application/json';
        break;

      case ExportFormat.CSV:
        const headers = ['#', 'Nome', 'Cidade', 'Bairro', 'Estado', 'Endereço Completo', 'Telefone', 'WhatsApp', 'Email', 'Website', 'Latitude', 'Longitude'];
        const rows = dataToExport.map((b, idx) => [
          idx + 1,
          b.name,
          b.city || '',
          b.neighborhood || '',
          b.state || '',
          b.address,
          b.phone || '',
          b.whatsapp || '',
          b.email || '',
          b.website || '',
          b.coordinates.lat,
          b.coordinates.lng
        ]);
        
        content = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        filename = selectedRows.size > 0 ? 'resultados_selecionados.csv' : 'resultados_busca.csv';
        mimeType = 'text/csv;charset=utf-8;';
        break;
    }

    // Download do arquivo
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const getQualityColor = (quality: number): 'error' | 'warning' | 'success' => {
    if (quality < 40) return 'error';
    if (quality < 70) return 'warning';
    return 'success';
  };

  // Aplicar filtros
  const filteredResults = results.filter(b => {
    // Filtro de qualidade
    if (b.dataQuality < filterQuality) return false;
    
    // Filtros por tipo de dado disponível
    if (filterHasWebsite && !b.website) return false;
    if (filterHasWhatsapp && !b.whatsapp) return false;
    if (filterHasPhone && !b.phone) return false;
    if (filterHasEmail && !b.email) return false;
    
    return true;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'distance':
        // Implementar cálculo de distância se necessário
        return 0;
      default:
        return 0;
    }
  });

  // Reagir ao trigger de exportação
  useEffect(() => {
    if (exportTrigger) {
      if (exportTrigger.type === 'csv') {
        handleExport(ExportFormat.CSV);
      } else if (exportTrigger.type === 'json') {
        handleExport(ExportFormat.JSON);
      }
      if (onExportComplete) {
        onExportComplete();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportTrigger]);

  // Notificar mudanças nas contagens
  useEffect(() => {
    if (onCountsChange) {
      onCountsChange(sortedResults.length, selectedRows.size);
    }
  }, [sortedResults.length, selectedRows.size, onCountsChange]);

  if (isSearching) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
        <Alert severity="info">Buscando estabelecimentos...</Alert>
      </Paper>
    );
  }

  if (results.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
        <Alert severity="info">
          Nenhum resultado ainda. Realize uma busca para ver os estabelecimentos.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {selectedRows.size > 0 && (
        <Box sx={{ p: 2, pb: 1 }}>
          <Alert severity="info">
            ✅ {selectedRows.size} {selectedRows.size === 1 ? 'linha selecionada' : 'linhas selecionadas'}
            <Button size="small" onClick={() => setSelectedRows(new Set())} sx={{ ml: 2 }}>
              Desmarcar Todas
            </Button>
          </Alert>
        </Box>
      )}

      {(viewMode === 'table' || tableOnly) ? (
        <TableContainer sx={{ overflow: 'auto', flex: 1 }}>
          <Table 
            stickyHeader 
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                py: 0.5,
                px: 1,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap'
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: 'primary.main', color: 'white', width: 40 }}>
                  <Checkbox
                    size="small"
                    indeterminate={selectedRows.size > 0 && selectedRows.size < sortedResults.length}
                    checked={sortedResults.length > 0 && selectedRows.size === sortedResults.length}
                    onChange={handleSelectAll}
                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 40 }}>#</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 150 }}>Nome</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 150 }}>Cidade / Bairro</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 200 }}>Endereço Completo</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 130 }}>Telefone</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 150 }}>Website</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 80 }}>Avaliação</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 80 }}>Qualidade</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedResults.map((business, index) => {
                const isItemSelected = isSelected(business.id);
                return (
                <TableRow 
                  key={business.id} 
                  hover 
                  role="checkbox"
                  aria-checked={isItemSelected}
                  selected={isItemSelected}
                  onClick={() => handleSelectRow(business.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isItemSelected}
                      onChange={() => handleSelectRow(business.id)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight="bold" color="primary">
                      {index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={business.name.length > 34 ? business.name : ''}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <BusinessIcon color="primary" fontSize="small" />
                        <Typography 
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 200
                          }}
                        >
                          {business.name.length > 34 ? business.name.substring(0, 34) + '...' : business.name}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.3}>
                      {business.city && (
                        <Typography variant="body2" fontWeight="medium">
                          {business.city}{business.state ? ` - ${business.state}` : ''}
                        </Typography>
                      )}
                      {business.neighborhood && (
                        <Typography variant="caption" color="text.secondary">
                          {business.neighborhood}
                        </Typography>
                      )}
                      {!business.city && !business.neighborhood && (
                        <Typography variant="caption" color="text.disabled">
                          Não informado
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={business.address}>
                      <Typography sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {business.address}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {business.phone ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography>{business.phone}</Typography>
                        {business.whatsapp && (
                          <Tooltip title="Abrir WhatsApp">
                            <IconButton
                              size="small"
                              color="success"
                              sx={{ p: 0.3 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`https://wa.me/${business.whatsapp?.replace(/\D/g, '') || ''}`, '_blank');
                              }}
                            >
                              <WhatsAppIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {business.website ? (
                      <Tooltip title={business.website}>
                        <Typography
                          sx={{
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(business.website, '_blank');
                          }}
                        >
                          {business.website}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {business.rating ? (
                      <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="center">
                        <StarIcon fontSize="small" color="warning" sx={{ fontSize: '0.9rem' }} />
                        <Typography>{business.rating.toFixed(1)}</Typography>
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${business.dataQuality}%`}
                      color={getQualityColor(business.dataQuality)}
                      size="small"
                      sx={{ fontSize: '0.65rem', height: 18, minWidth: 45 }}
                    />
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <List sx={{ overflow: 'auto', flex: 1, mt: 2 }}>
          {sortedResults.map((business) => (
          <ListItem key={business.id} sx={{ px: 0, py: 1 }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" component="div">
                    {business.name}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
                  <Chip
                    label={`${business.dataQuality}%`}
                    color={getQualityColor(business.dataQuality)}
                    size="small"
                  />
                  {business.rating && (
                    <Chip
                      icon={<StarIcon />}
                      label={`${business.rating.toFixed(1)} (${business.userRatingsTotal})`}
                      size="small"
                    />
                  )}
                  <Chip
                    label={business.source}
                    size="small"
                    variant="outlined"
                  />
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {business.address}
                    </Typography>
                  </Stack>

                  {business.phone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">{business.phone}</Typography>
                    </Stack>
                  )}

                  {business.whatsapp && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WhatsAppIcon fontSize="small" color="success" />
                      <Typography variant="body2">{business.whatsapp}</Typography>
                    </Stack>
                  )}

                  {business.email && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{business.email}</Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>

              <CardActions>
                {business.website && (
                  <Tooltip title="Visitar website">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(business.website, '_blank')}
                    >
                      <LanguageIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {business.phone && (
                  <Tooltip title="Ligar">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(`tel:${business.phone}`, '_self')}
                    >
                      <PhoneIcon />
                    </IconButton>
                  </Tooltip>
                )}
                  {business.whatsapp && (
                    <Tooltip title="WhatsApp">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() =>
                          window.open(`https://wa.me/${business.whatsapp?.replace(/\D/g, '') || ''}`, '_blank')
                        }
                      >
                        <WhatsAppIcon />
                      </IconButton>
                    </Tooltip>
                  )}
              </CardActions>
            </Card>
          </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default ResultsList;

